/* eslint-disable react-hooks/exhaustive-deps */
// TODO: Remove the above line and solve the underlying issues
import { ReviewWidget } from '@/components/BazaarVoice';
import NextHead from '@/components/NextHead';
import { hydrateColor } from '@/pages/ColorDetails/helper';
import { ProductDescriptions } from '@/pages/ProductDetails/styled';
import metas from '@/routes/meta';
import { hasColors, isAccessory, isColorCard } from '@/util/product';
import {
  Color,
  H2,
  Paragraph,
  Product,
  Wrapper,
  bffService,
  useColor,
  useProduct,
} from '@thisisdevelopment/akzonobel-core';
import { CmsVideo, Page, ProductColor } from '@thisisdevelopment/heritage-core-library';
import { groupBy } from 'lodash';
import { GetStaticPaths, GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import { fetchColors } from 'hooks/useColors';
import { useEffect, useMemo } from 'react';
import { pageRevalidate } from '@/config';
import { OurProductsType } from '../index';
import { DocumentDownloadList } from 'components/DocumentDownloadList';
import { PageUnavailable } from '@/components/PageUnavailable';

type SSRProps = {
  page: OurProductsType | null;
  product: any;
  colorsGrouped: { [key: string]: Color[] };
};

const ProductDetails = ({ page, product: initialProduct, colorsGrouped }: SSRProps) => {
  const { product: fetchedProduct, fetchProduct } = useProduct();
  const product = fetchedProduct || initialProduct;
  const router = useRouter();
  const { color, fetchColor, isLoading: isColorLoading } = useColor();
  let cccid = router.query?.cccid?.toString() || color?.colorCollectionColorId.toString();
  const pickAColour = router.query?.pickAColour?.toString();

  useEffect(() => {
    if (cccid) {
      const url = {
        pathname: router.pathname,
        query: {
          ...router.query,
          cccid,
        },
      };
      router.replace(url, undefined, {
        shallow: true,
      });
      fetchProduct({ friendlyName: router.query.friendlyName as string }, `cccid=${cccid}`);
    }
  }, [cccid, router.query.friendlyName]);

  useEffect(() => {
    if (!cccid && product?.colorCollectionColorIds[0]) {
      cccid = product?.colorCollectionColorIds[0];
    }
  });

  useEffect(() => {
    if (!router.isReady || isColorLoading) return;
    if (product && cccid && color?.colorCollectionColorId.toString() !== cccid) {
      if (product.colorCollectionColorIds.includes(Number(cccid))) {
        fetchColor(cccid);
      } else {
        router.replace('/notfound');
      }
    }
  }, [cccid, product, fetchColor, router, isColorLoading]);

  const colorHydrated = useMemo(() => color && hydrateColor(color), [color]);
  const meta = metas.products;

  const availableColorsGrouped = Object.fromEntries(
    Object.entries(colorsGrouped)
      .map(([key, colors]) => [
        key,
        colors.filter((color) =>
          product.colorCollectionColorIds.includes(color.colorCollectionColorId),
        ),
      ])
      .filter(([, colors]) => colors.length > 0),
  );

  if (!page) return <PageUnavailable />;

  return (
    <div style={{ overflow: 'auto' }}>
      <NextHead meta={meta} />
      <Page title={page.title}>
        <ProductColor
          mode={'Product'}
          product={product}
          color={color}
          colorsGrouped={availableColorsGrouped}
          backgroundImage={
            hasColors(product)
              ? colorHydrated?.inspirationalImages?.[0].url
              : page.product_list_background
          }
          openColors={pickAColour === 'true'}
        />
        {!isColorCard(product) && !isAccessory(product) && (
          <Wrapper>
            <Wrapper>
              <ProductDescriptions>
                <Wrapper>
                  <H2>Application description</H2>
                  <Paragraph>{product.applicationDescription}</Paragraph>
                </Wrapper>
                <Wrapper>
                  <H2>Surface preparation</H2>
                  <Paragraph>{product.surfaceDescription}</Paragraph>
                </Wrapper>
                <Wrapper>
                  <H2>Storage</H2>
                  <Paragraph>{product.storage}</Paragraph>
                </Wrapper>
              </ProductDescriptions>
            </Wrapper>
            <DocumentDownloadList product={product} title={'Health & Safety'} />
          </Wrapper>
        )}
        <CmsVideo coverId={page.video_cover} videoId={page.video} mb={'50px'} />
        {product && <ReviewWidget id={product.globalId} type={'reviews'} bgColor={'#fff'} />}
      </Page>
    </div>
  );
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const product = await bffService
    .getProductByFriendlyName(params?.friendlyName as string)
    .then((res) => res.data)
    .catch(() => null);
  const page = (await bffService
    .getCmsItem('our_products')
    .then((res) => res.data.data[0])
    .catch(() => null)) as OurProductsType | null;

  if (!page || !product) {
    return { notFound: true };
  }

  const colors = await fetchColors('', { flip90AntiClockwise: true });
  const colorsGrouped = groupBy(colors, 'sensation');
  return {
    props: { page, product: product, colorsGrouped },
    revalidate: pageRevalidate,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const products = (await bffService
    .getProducts()
    .then((res) => res.data.products)
    .catch(() => [])) as Product[];
  const paths = products.map((p) => ({
    params: { friendlyName: p.uriFriendlyName },
  }));

  return {
    paths,
    fallback: false,
  };
};

export default ProductDetails;
